import React, { useState, useEffect } from 'react';
import { permissionsAPI, gestionnairesAPI } from '../services/api';
import { 
  Users, UserPlus, Edit, Trash2, Save, 
  Shield, Power, PowerOff, RefreshCw, History,
  LogIn, FileText, Upload, MessageSquare, Eye
} from 'lucide-react';

const TeamManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('team');
  
  // Data
  const [gestionnaires, setGestionnaires] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [referentiels, setReferentiels] = useState({ permissions: {}, sections: [], champs_sensibles: [] });
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityStats, setActivityStats] = useState(null);
  const [selectedUserActivity, setSelectedUserActivity] = useState(null);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Formulaire création/édition
  const [formData, setFormData] = useState({
    nom: '', prenom: '', email: '', password: '',
    type_gestionnaire: 'gestionnaire', avatar_color: '#3b82f6'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [gestRes, profilesRes, refRes] = await Promise.all([
        gestionnairesAPI.getAll(),
        permissionsAPI.getProfiles(),
        permissionsAPI.getReferentiels()
      ]);
      setGestionnaires(gestRes.data);
      setProfiles(profilesRes.data);
      setReferentiels(refRes.data);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityLogs = async () => {
    try {
      const [logsRes, statsRes] = await Promise.all([
        permissionsAPI.getActivityLogs({ limit: 100 }),
        permissionsAPI.getActivityStats()
      ]);
      setActivityLogs(logsRes.data);
      setActivityStats(statsRes.data);
    } catch (error) {
      console.error('Erreur chargement activité:', error);
    }
  };

  const loadUserActivity = async (user) => {
    try {
      const res = await permissionsAPI.getUserActivity(user.id, 50);
      setSelectedUserActivity({ user, logs: res.data });
      setShowActivityModal(true);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // Créer un gestionnaire
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.nom || !formData.prenom || !formData.email || !formData.password) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSaving(true);
    try {
      const profile = profiles.find(p => p.code === formData.type_gestionnaire);
      
      const response = await gestionnairesAPI.create({
        nom: formData.nom,
        prenom: formData.prenom,
        email: formData.email,
        password: formData.password,
        role: formData.type_gestionnaire === 'admin' ? 'super_admin' : 'gestionnaire',
        avatar_color: formData.avatar_color
      });
      
      if (profile) {
        await permissionsAPI.updateGestionnairePermissions(response.data.id, {
          profile_id: profile.id
        });
      }
      
      await loadData();
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  // Modifier un gestionnaire
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    
    setSaving(true);
    try {
      const updateData = {
        nom: formData.nom,
        prenom: formData.prenom,
        email: formData.email,
        avatar_color: formData.avatar_color
      };
      
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await gestionnairesAPI.update(editingUser.id, updateData);
      
      const profile = profiles.find(p => p.code === formData.type_gestionnaire);
      if (profile) {
        await permissionsAPI.updateGestionnairePermissions(editingUser.id, {
          profile_id: profile.id
        });
      }
      
      await loadData();
      setShowEditModal(false);
      setEditingUser(null);
      resetForm();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  // Activer/Désactiver
  const toggleActive = async (user) => {
    try {
      await gestionnairesAPI.update(user.id, { actif: !user.actif });
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // Supprimer
  const handleDelete = async (user) => {
    if (user.role === 'super_admin' && gestionnaires.filter(g => g.role === 'super_admin').length <= 1) {
      alert('Impossible de supprimer le dernier administrateur');
      return;
    }
    if (!window.confirm(`Supprimer ${user.prenom} ${user.nom} ?`)) return;
    
    try {
      await gestionnairesAPI.delete(user.id);
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // Ouvrir modal édition
  const openEditModal = async (user) => {
    setEditingUser(user);
    try {
      const permRes = await permissionsAPI.getGestionnairePermissions(user.id);
      const currentProfile = permRes.data.profile;
      setFormData({
        nom: user.nom, prenom: user.prenom, email: user.email, password: '',
        type_gestionnaire: currentProfile?.code || 'gestionnaire',
        avatar_color: user.avatar_color || '#3b82f6'
      });
    } catch (error) {
      setFormData({
        nom: user.nom, prenom: user.prenom, email: user.email, password: '',
        type_gestionnaire: 'gestionnaire', avatar_color: user.avatar_color || '#3b82f6'
      });
    }
    setShowEditModal(true);
  };

  // Ouvrir modal permissions
  const openPermissionsModal = async (user) => {
    try {
      const permRes = await permissionsAPI.getGestionnairePermissions(user.id);
      setEditingUser({ ...user, ...permRes.data });
      setShowPermissionsModal(true);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // Sauvegarder permissions
  const saveCustomPermissions = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await permissionsAPI.updateGestionnairePermissions(editingUser.gestionnaire.id, {
        profile_id: editingUser.gestionnaire.profile_id,
        permissions_custom: editingUser.gestionnaire.permissions_custom || {}
      });
      await loadData();
      setShowPermissionsModal(false);
      setEditingUser(null);
    } catch (error) {
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '', prenom: '', email: '', password: '',
      type_gestionnaire: 'gestionnaire', avatar_color: '#3b82f6'
    });
  };

  const getUserProfile = (user) => profiles.find(p => p.id === user.profile_id);

  // Stats
  const stats = {
    total: gestionnaires.length,
    actifs: gestionnaires.filter(g => g.actif).length,
    inactifs: gestionnaires.filter(g => !g.actif).length,
  };

  // Types de gestionnaires
  const typeOptions = [
    { code: 'admin', label: 'Administrateur', color: '#7c3aed', description: 'Accès complet' },
    { code: 'senior', label: 'Confirmé / Senior', color: '#059669', description: 'Presque tous les droits' },
    { code: 'gestionnaire', label: 'Gestionnaire', color: '#3b82f6', description: 'Droits standards' },
    { code: 'junior', label: 'Junior / Stagiaire', color: '#f59e0b', description: 'Accès limité' },
  ];

  // Icône par type d'activité
  const getActivityIcon = (type) => {
    switch (type) {
      case 'connexion': return <LogIn className="w-4 h-4" />;
      case 'dossier_creation': return <FileText className="w-4 h-4" />;
      case 'dossier_modification': return <Edit className="w-4 h-4" />;
      case 'dossier_consultation': return <Eye className="w-4 h-4" />;
      case 'document_upload': return <Upload className="w-4 h-4" />;
      case 'commentaire': return <MessageSquare className="w-4 h-4" />;
      default: return <History className="w-4 h-4" />;
    }
  };

  // Couleur par type d'activité
  const getActivityColor = (type) => {
    switch (type) {
      case 'connexion': return 'bg-green-100 text-green-700';
      case 'dossier_creation': return 'bg-blue-100 text-blue-700';
      case 'dossier_modification': return 'bg-yellow-100 text-yellow-700';
      case 'dossier_consultation': return 'bg-gray-100 text-gray-700';
      case 'document_upload': return 'bg-purple-100 text-purple-700';
      case 'export': return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="w-8 h-8 text-green-600" />
            Gestion de l'Équipe
          </h1>
          <p className="text-gray-600 mt-1">Gérez vos gestionnaires et suivez leur activité</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowAddModal(true); }}
          className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition flex items-center gap-2"
          data-testid="add-user-btn"
        >
          <UserPlus className="w-5 h-5" />
          Ajouter
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('team')}
              className={`flex items-center gap-2 px-6 py-4 font-medium border-b-2 transition ${
                activeTab === 'team'
                  ? 'border-green-600 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="w-5 h-5" />
              Équipe
              <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">{stats.total}</span>
            </button>
            <button
              onClick={() => { setActiveTab('activity'); loadActivityLogs(); }}
              className={`flex items-center gap-2 px-6 py-4 font-medium border-b-2 transition ${
                activeTab === 'activity'
                  ? 'border-green-600 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <History className="w-5 h-5" />
              Historique d'activité
            </button>
          </nav>
        </div>

        {/* Tab Équipe */}
        {activeTab === 'team' && (
          <div className="p-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
                <div className="text-3xl font-bold">{stats.total}</div>
                <div className="text-green-100">Total gestionnaires</div>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
                <div className="text-3xl font-bold">{stats.actifs}</div>
                <div className="text-blue-100">Actifs</div>
              </div>
              <div className="bg-gradient-to-br from-gray-400 to-gray-500 rounded-xl p-5 text-white">
                <div className="text-3xl font-bold">{stats.inactifs}</div>
                <div className="text-gray-100">Inactifs</div>
              </div>
            </div>

            {/* Légende */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex flex-wrap gap-4">
                {typeOptions.map(type => (
                  <div key={type.code} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }} />
                    <span className="text-sm font-medium" style={{ color: type.color }}>{type.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Table */}
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Gestionnaire</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-gray-700">Type</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-gray-700">Dossiers</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-gray-700">Statut</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {gestionnaires.map(user => {
                  const profile = getUserProfile(user);
                  const typeInfo = typeOptions.find(t => t.code === profile?.code) || typeOptions[2];
                  
                  return (
                    <tr key={user.id} className={`hover:bg-gray-50 ${!user.actif ? 'opacity-50' : ''}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: user.avatar_color || '#3b82f6' }}
                          >
                            {user.prenom?.[0]}{user.nom?.[0]}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{user.prenom} {user.nom}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                            {user.derniere_connexion && (
                              <div className="text-xs text-gray-400">
                                Dernière connexion: {formatDate(user.derniere_connexion)}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span 
                          className="px-3 py-1.5 rounded-full text-sm font-medium"
                          style={{ backgroundColor: typeInfo.color + '15', color: typeInfo.color }}
                        >
                          {profile?.nom || typeInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-2xl font-bold text-gray-900">{user.dossiers_count || 0}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => toggleActive(user)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition ${
                            user.actif ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                        >
                          {user.actif ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                          {user.actif ? 'Actif' : 'Inactif'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => openEditModal(user)} className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Modifier">
                            <Edit className="w-5 h-5" />
                          </button>
                          <button onClick={() => openPermissionsModal(user)} className="p-2 text-gray-600 hover:text-violet-600 hover:bg-violet-50 rounded-lg" title="Permissions">
                            <Shield className="w-5 h-5" />
                          </button>
                          <button onClick={() => loadUserActivity(user)} className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg" title="Historique">
                            <History className="w-5 h-5" />
                          </button>
                          {user.role !== 'super_admin' && (
                            <button onClick={() => handleDelete(user)} className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Supprimer">
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Tab Historique */}
        {activeTab === 'activity' && (
          <div className="p-6">
            {/* Stats d'activité */}
            {activityStats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
                  <div className="text-3xl font-bold">{activityStats.connexions_today}</div>
                  <div className="text-green-100">Connexions aujourd'hui</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
                  <div className="text-3xl font-bold">{activityStats.activity_by_type?.length || 0}</div>
                  <div className="text-blue-100">Types d'actions</div>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
                  <div className="text-3xl font-bold">{activityStats.top_users?.length || 0}</div>
                  <div className="text-purple-100">Utilisateurs actifs</div>
                </div>
              </div>
            )}

            {/* Top utilisateurs */}
            {activityStats?.top_users?.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Utilisateurs les plus actifs</h3>
                <div className="flex flex-wrap gap-3">
                  {activityStats.top_users.slice(0, 5).map((u, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border">
                      <span className="font-medium text-gray-900">{u.user_nom}</span>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{u.count} actions</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Liste des activités */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Journal d'activité</h3>
              <button onClick={loadActivityLogs} className="text-green-600 hover:text-green-700 flex items-center gap-1 text-sm">
                <RefreshCw className="w-4 h-4" />
                Actualiser
              </button>
            </div>

            {activityLogs.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">Aucune activité enregistrée</p>
                <p className="text-sm text-gray-400 mt-1">Les connexions et actions seront affichées ici</p>
              </div>
            ) : (
              <div className="space-y-2">
                {activityLogs.map((log, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-4 flex items-center gap-4 hover:bg-gray-100 transition">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getActivityColor(log.type)}`}>
                      {getActivityIcon(log.type)}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{log.description}</div>
                      <div className="text-sm text-gray-500">
                        {log.user_nom}
                        {log.ressource_id && <span className="text-gray-400"> • {log.ressource_type}</span>}
                      </div>
                    </div>
                    <div className="text-sm text-gray-400">{formatDate(log.date)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Ajouter */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <UserPlus className="w-6 h-6 text-green-600" />
                Ajouter un gestionnaire
              </h2>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                  <input type="text" value={formData.prenom} onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input type="text" value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe *</label>
                <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
                <div className="grid grid-cols-2 gap-2">
                  {typeOptions.map(type => (
                    <label key={type.code}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${
                        formData.type_gestionnaire === type.code ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                      <input type="radio" name="type" value={type.code} checked={formData.type_gestionnaire === type.code}
                        onChange={(e) => setFormData({ ...formData, type_gestionnaire: e.target.value })} className="hidden" />
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: type.color }} />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">{type.label}</div>
                        <div className="text-xs text-gray-500">{type.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50">Annuler</button>
                <button type="submit" disabled={saving}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Modifier */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Modifier {editingUser.prenom} {editingUser.nom}</h2>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <input type="text" value={formData.prenom} onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <input type="text" value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe (optionnel)</label>
                <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {typeOptions.map(type => (
                    <label key={type.code}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer ${
                        formData.type_gestionnaire === type.code ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}>
                      <input type="radio" name="type_edit" value={type.code} checked={formData.type_gestionnaire === type.code}
                        onChange={(e) => setFormData({ ...formData, type_gestionnaire: e.target.value })} className="hidden" />
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: type.color }} />
                      <div className="text-sm font-medium">{type.label}</div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => { setShowEditModal(false); setEditingUser(null); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50">Annuler</button>
                <button type="submit" disabled={saving}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Permissions */}
      {showPermissionsModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Permissions de {editingUser.gestionnaire?.prenom} {editingUser.gestionnaire?.nom}</h2>
              <p className="text-sm text-gray-500 mt-1">Personnalisez les permissions (surcharge le profil par défaut)</p>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {editingUser.profile && (
                <div className="bg-violet-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-violet-700">Profil: {editingUser.profile.nom}</h3>
                      <p className="text-sm text-violet-600">{Object.values(editingUser.profile.permissions || {}).filter(Boolean).length} permissions actives</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(
                  Object.entries(referentiels.permissions).reduce((acc, [key, val]) => {
                    const cat = val.categorie || 'autre';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push({ key, ...val });
                    return acc;
                  }, {})
                ).map(([category, perms]) => (
                  <div key={category} className="border rounded-lg p-3">
                    <h4 className="font-medium text-gray-700 mb-2 capitalize">{category}</h4>
                    <div className="space-y-1">
                      {perms.map(perm => {
                        const profileValue = editingUser.profile?.permissions?.[perm.key];
                        const customValue = editingUser.gestionnaire?.permissions_custom?.[perm.key];
                        const effectiveValue = customValue !== undefined ? customValue : profileValue;
                        return (
                          <label key={perm.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                            <input type="checkbox" checked={effectiveValue || false}
                              onChange={(e) => {
                                const newCustom = { ...editingUser.gestionnaire?.permissions_custom };
                                newCustom[perm.key] = e.target.checked;
                                setEditingUser({
                                  ...editingUser,
                                  gestionnaire: { ...editingUser.gestionnaire, permissions_custom: newCustom }
                                });
                              }}
                              className="w-4 h-4 text-violet-600 rounded" />
                            <span className={customValue !== undefined ? 'font-medium text-violet-700' : ''}>
                              {perm.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button onClick={() => { setShowPermissionsModal(false); setEditingUser(null); }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50">Annuler</button>
              <button onClick={saveCustomPermissions} disabled={saving}
                className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Historique utilisateur */}
      {showActivityModal && selectedUserActivity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <History className="w-6 h-6 text-green-600" />
                Activité de {selectedUserActivity.user.prenom} {selectedUserActivity.user.nom}
              </h2>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {selectedUserActivity.logs.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Aucune activité enregistrée</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedUserActivity.logs.map((log, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-4 flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getActivityColor(log.type)}`}>
                        {getActivityIcon(log.type)}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{log.description}</div>
                        {log.ressource_id && (
                          <div className="text-sm text-gray-500">{log.ressource_type}</div>
                        )}
                      </div>
                      <div className="text-sm text-gray-400">{formatDate(log.date)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200">
              <button onClick={() => { setShowActivityModal(false); setSelectedUserActivity(null); }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;
